import React from 'react';

import * as RadixSwitch from '@radix-ui/react-switch';
import type { SwitchProps as RadixSwitchProps } from '@radix-ui/react-switch';
import cn from 'classnames';

import { BaseComponentProps } from '../../../types/common';
import { H6, HeadingsProps } from '../Typography';

import styles from './Switch.module.css';

export type SwitchProps = BaseComponentProps & {
  /**
   * The checked state of the switch
   */
  checked?: RadixSwitchProps['checked'];
  /**
   * The callback function that is called when the switch is checked or unchecked
   */
  onCheckedChange?: RadixSwitchProps['onCheckedChange'];
  /**
   * Whether the switch is disabled
   */
  disabled?: RadixSwitchProps['disabled'];
  /**
   * Placement of the label with respect to the switch
   */
  justifyContent?: React.CSSProperties['justifyContent'];
  /**
   * Margin around the switch container
   */
  margin?: React.CSSProperties['margin'];
  /**
   * Color of the switch label
   */
  color?: HeadingsProps['color'];
} & (
    | {
        /**
         * The unique identifier of the switch
         * Please, make sure to pass the unique `id` prop to the component
         */
        id: string;
        /**
         * Label text to display near the switch
         * In that case, the `id` prop is required to ensure the label is associated with the switch
         */
        label: string;
        ariaLabel?: never;
      }
    | {
        id?: never;
        label?: never;
        /**
         * Required aria-label when no visible label is provided
         */
        ariaLabel: string;
      }
  );

/**
 * Component with proper styling for switch.
 */
export const Switch: React.FC<SwitchProps> = ({
  id,
  checked,
  onCheckedChange,
  label,
  ariaLabel,
  justifyContent = 'center',
  color = 'default',
  margin,
  disabled,
  dataTestId,
}) => {
  return (
    <div
      className={styles.switch}
      data-testid={dataTestId}
      style={{ margin, justifyContent }}
    >
      {label && (
        <label className={styles.label} htmlFor={id}>
          <H6 as="span" color={disabled ? 'disabled' : color}>
            {label}
          </H6>
        </label>
      )}
      <RadixSwitch.Root
        aria-label={!label ? ariaLabel : undefined}
        checked={checked}
        className={cn(styles['radix-switch-root'], {
          // If the `onCheckedChange` prop is not provided, the switch will not be hoverable (interactive)
          // since it's not possible to change the checked state
          [styles['radix-switch-root-hoverable']]: onCheckedChange,
        })}
        disabled={disabled}
        id={id}
        onCheckedChange={onCheckedChange}
      >
        <RadixSwitch.Thumb className={styles.thumb} />
      </RadixSwitch.Root>
    </div>
  );
};
